import React, { useEffect, useState, useContext } from "react";
import { formatDateWithYear } from "../../../utils/hooks/dataHandler";
import { AuthContext } from "../../../contexts/AuthContext";
import HirePurchaseSchedule from "../../../components/Schedules/CA/HirePurchase/HirePurchaseSchedule";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { getCaSettings } from "../../../builders/settings/CaSetting";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { CA_SCHEDULES } from "../../../utils/data/controlData";

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getRegisteredAssetCategoryId = (assetCategory) =>
  `fa-register-${String(assetCategory || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

const getCategoryIdForRegisteredAsset = (assetCategory, assetCat = []) => {
  const existingCategory = assetCat.find(
    (item) => normalizeText(item.assetCategory) === normalizeText(assetCategory)
  );

  return (
    existingCategory?.assetId || getRegisteredAssetCategoryId(assetCategory)
  );
};

const buildRegisteredHirePurchaseItems = (
  registeredHirePurchaseAssets = [],
  assetCat = [],
  faRegisterSource = null
) =>
  registeredHirePurchaseAssets.map((asset, index) => {
    const assetCategory = asset.assetCategory || "";
    const rawSourceAssetId = asset.assetId || asset.rowKey || index;
    const sourceAssetId = String(rawSourceAssetId).startsWith(
      "fa-register-addition-"
    )
      ? rawSourceAssetId
      : `fa-register-addition-${rawSourceAssetId}`;

    return {
      assetId: sourceAssetId,
      assetName:
        asset.assetCategory ||
        asset.assetDescription ||
        `Registered HP Asset ${index + 1}`,
      description: asset.assetDescription || "",
      categoryId: assetCategory
        ? getCategoryIdForRegisteredAsset(assetCategory, assetCat)
        : undefined,
      category: assetCategory,
      cost: Number(asset.costOfAsset) || 0,
      caType: "",
      noOfYears: "",
      principalPaid: [],
      source: "FA Register",
      sourceSchedule: faRegisterSource?.scheduleNo
        ? `Sch ${faRegisterSource.scheduleNo}`
        : "FA Register",
      faRegisterSource: true,
    };
  });

const tagHirePurchaseImportSource = (items = [], source, sourceSchedule) =>
  items.map((item) => ({
    ...item,
    source,
    sourceSchedule,
  }));

const HirePurchasePage = ({
  uen,
  scheduleId,
  companyName,
  yearOfAssessment,
  financialYearStart,
  financialYearEnd,
}) => {
  const { getCAScheduleDataByType, updateCASchedule, deleteCAScheduleByType } =
    useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditCASchedule } = useAccessControl(CA_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!uen || !scheduleId) {
      handleError("Missing UEN or Schedule ID. Please try again.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "HirePurchase",
      dataRows: scheduleDetails.dataRows,
      computedData: scheduleDetails.computedData,
      doc: scheduleDetails.doc,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
    };

    setLoading(true);
    const response = await updateCASchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const response = await getCAScheduleDataByType(scheduleId, 'HirePurchase');

        const scheduleData = response?.schedule;

        let caSettings = getCaSettings();
        if (scheduleData) {
          setSchedule({
            id: scheduleId,
            type: "Hire Purchase Schedule",
            scheduleNo: scheduleData.scheduleNo,
            name: "Hire Purchase Schedule",
            caSettings: scheduleData.caSetting || caSettings,
            refSources: scheduleData.scheduleMapping || {},
            notes: scheduleData.notes || "",
            reviewNotes: scheduleData.reviewNotes || "",
          });

          setScheduleDetails({
            dataRows: {
              assets: scheduleData.assets || [],
              settings: scheduleData.settings || {
                deferredYears: [],
                assetCodeList: [],
                codePrefix: "HPA",
              },
            },
            categories: scheduleData.assetCat,
            additionsHpItems: [
              ...tagHirePurchaseImportSource(
                scheduleData.hirePurchaseSummary || [],
                "FA Additions",
                scheduleData.scheduleMapping?.FAAdditions
                  ? `Sch ${scheduleData.scheduleMapping.FAAdditions}`
                  : undefined
              ),
              ...buildRegisteredHirePurchaseItems(
                scheduleData.registeredHirePurchaseAssets ||
                  scheduleData.faRegisterImport?.hirePurchase ||
                  [],
                scheduleData.assetCat || [],
                scheduleData.faRegisterImport?.source
              ),
            ],
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    if (uen && scheduleId) {
      fetchData();
    }
  }, [saveTrigger, scheduleId, uen]);

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteCAScheduleByType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <HirePurchaseSchedule
          companyName={companyName}
          uen={uen}
          yearOfAssessment={yearOfAssessment}
          financialYearStart={formatDateWithYear(financialYearStart)}
          financialYearEnd={formatDateWithYear(financialYearEnd)}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditCASchedule}
        />
      )}
    </>
  );
};

export default HirePurchasePage;
